import React, {
  FC,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import { selectUserRole } from '@/app/store/selectors/selectUserId';
import { RoleType } from '@/modules/authentification/types/RoleType';
import { useCurrentEmployee } from '@/modules/authentification/hooks';
import { menuEmployeeItems } from '@/modules/layout/constants/ConstantsEmployeeMenu';
import Layout from '@/modules/layout/Layout';
import { ButtonPrimary } from '@common/components/buttons';
import { MainCard } from '@common/components/mainCard';
import { useTranslation } from 'react-i18next';

export interface TupWallProps {}

function interpolate(
  content: string,
  { destination, email }: { destination: string; email: string },
) {
  return content
    .replace('%{destination}', destination)
    .replace('%{email}', email);
}

function destinationUrl(destination: string | undefined): string {
  switch (destination) {
    case 'digiportage':
      return 'https://extranet.digiportage.com?or=prium';
    case 'valor':
      return 'https://extranet.valorconnect.com?or=prium';
    default:
      return '#';
  }
}

const TupWallContent: FC<{
  isAdmin: boolean;
  handleForce: () => void;
}> = ({ isAdmin, handleForce }) => {
  const { t } = useTranslation('myNamespace');

  const { employee } = useCurrentEmployee();

  const intOpts = {
    destination: t(
      `activerecord.enums.employee.tup_destination.${employee?.tupDestination}`,
      { ns: 'rails' },
    ),
    email: employee?.email ?? '',
  };

  return (
    <div className="col-12">
      <MainCard
        header={
          <span
            dangerouslySetInnerHTML={{
              __html: interpolate(
                t('views.tup.wall.header', {
                  ns: 'rails',
                }),
                intOpts,
              ),
            }}
          />
        }
        className="mt-[20px] mx-5 md:w-[50%]"
      >
        <p
          dangerouslySetInnerHTML={{
            __html: interpolate(
              t('views.tup.wall.content', {
                ns: 'rails',
              }),
              intOpts,
            ),
          }}
        />
      </MainCard>
      <div className="mx-5">
        <div className="flex gap-4 px-[20px]">
          <ButtonPrimary href={destinationUrl(employee?.tupDestination)}>
            {interpolate(
              t('views.tup.wall.button', {
                ns: 'rails',
              }),
              intOpts,
            )}
          </ButtonPrimary>{' '}
          {isAdmin ? (
            <ButtonPrimary
              onClick={handleForce}
              className="bg-color-failure hover:bg-color-txt-failure hover:border-color-txt-failure"
            >
              {' '}
              Accès administrateur{' '}
            </ButtonPrimary>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const TupWall: FunctionComponent<PropsWithChildren<TupWallProps>> = ({
  children,
}) => {
  const [forced, setForced] = useState(false);
  const { employee } = useCurrentEmployee();
  const primaryRole = selectUserRole() as RoleType;
  const isAdmin = primaryRole !== 'employee';
  const handleForce = useCallback(() => setForced(true), [setForced]);

  const itemsMenu = menuEmployeeItems({ blank: true });

  if (forced || !employee?.isTupped) {
    return <>{children}</>;
  }

  return (
    <Layout itemsMenu={itemsMenu} logAsAdmin={isAdmin}>
      <TupWallContent handleForce={handleForce} isAdmin={isAdmin} />
    </Layout>
  );
};
